import { UntypedFormControl } from '@angular/forms';
import { controlContainsError, removeControlError } from '@controls/utils/control-validators-extender';
import { amountAs, makeAmountWith, reduceAmountWith, addition, ZERO_SF } from '@shared/utils/helpers/uom-calculator';
import { Amount } from 'uom';
import { Units } from 'uom-units';

interface ValueUOM {
  value: number;
  uom: string;
}

export const isExceeded = (min: number, minUOM: string, max: number, maxUOM: string): boolean => {
  let result = false;

  if (min != null && max != null) {
    const minObj = makeAmountWith({
      value: min,
      uom: minUOM,
      defaultUnit: Units.SquareFeet,
    });
    const minAmount = amountAs(Units.SquareFeet, minObj);

    const maxObj = makeAmountWith({
      value: max,
      uom: maxUOM,
      defaultUnit: Units.SquareFeet,
    });
    const maxAmount = amountAs(Units.SquareFeet, maxObj);

    result = minAmount > maxAmount;
  }

  return result;
};

export const isEqual = (min: number, minUOM: string, max: number, maxUOM: string): boolean => {
  let result = true;

  if (min != null && max != null) {
    const minObj = makeAmountWith({
      value: min,
      uom: minUOM,
      defaultUnit: Units.SquareFeet,
    });

    const maxObj = makeAmountWith({
      value: max,
      uom: maxUOM,
      defaultUnit: Units.SquareFeet,
    });

    result = Amount.equals(minObj, maxObj);
  }

  return result;
};

export function sumUOMsGetSquareFeet(items: ValueUOM[]): number {
  const amount = reduceAmountWith(items, {
    dataset: { value: 'value', uom: 'uom' },
    operation: addition,
    initialValue: ZERO_SF,
  });
  return amount.value;
}

export interface MinMaxAreaOptions {
  minField: string;
  minUom?: string;
  maxField: string;
  maxUom?: string;
  /** use it to substitute any missing uom */
  defaultUom?: string;
  errorMsg: string;
}

/**
 * Anything with value attribute (abstractControl, or any given object with it)
 */
type WithValue = { value: any };

/**
 * Validates whether if min amount is greater than max and returns given error,
 * when useReplacement is not null, it's used as "value" to substitute UOM absences
 * @param minCtrl
 * @param minUomCtrl
 * @param maxFieldCtrl
 * @param maxUomCtrl
 * @param useReplacement
 * @returns
 */
export const validateExceeding = (
  minCtrl: WithValue,
  minUomCtrl: WithValue,
  maxFieldCtrl: WithValue,
  maxUomCtrl: WithValue,
  useReplacement: WithValue
) => {
  let minUomValueHolder = minUomCtrl;
  let maxUomValueHolder = maxUomCtrl;
  if (useReplacement != null) {
    minUomValueHolder = minUomCtrl ?? useReplacement;
    maxUomValueHolder = maxUomCtrl ?? useReplacement;
  }
  return isExceeded(minCtrl.value, minUomValueHolder.value, maxFieldCtrl.value, maxUomValueHolder.value);
};

/**
 * Reactive form validations for Area (Amount) values
 */
export class AreaValidation {
  /**
   * Validates whether if min amount is greater than max and returns given error, if defaultUom value is
   * given, then minUom & maxUom attributes becomes optional since defaultUom value will be used as replacement
   * @param options
   * @returns
   */
  static minMax(options: MinMaxAreaOptions) {
    const { minField, minUom, maxField, maxUom, errorMsg, defaultUom } = options;
    return (control: UntypedFormControl) => {
      const parentForm = control.parent;
      // From ng11,  Parent is explicitly initialized with null instead of being left undefined
      // parentForm == null will compare both, null & undefined
      if (parentForm == null) {
        return null;
      }

      const minCtrl = parentForm.get(minField);
      const minUomCtrl = parentForm.get(minUom);
      const minControls = [minCtrl, minUomCtrl] as UntypedFormControl[];

      const maxCtrl = parentForm.get(maxField);
      const maxUomCtrl = parentForm.get(maxUom);
      const maxControls = [maxCtrl, maxUomCtrl] as UntypedFormControl[];

      const useReplacement = defaultUom == null ? null : { value: defaultUom };
      // all 4 controls (2 value & uom tuples) are required to make validation work
      const minAndMaxControlsWithUom = [minCtrl, minUomCtrl, maxCtrl, maxUomCtrl];
      // unless defaultUom value is given then uom controls become optional and only value controls are required
      const controlsValuesWithReplacement = [minCtrl, maxCtrl, useReplacement];

      if (minAndMaxControlsWithUom.every(Boolean) || controlsValuesWithReplacement.every(Boolean)) {
        const isInvalid = validateExceeding(minCtrl, minUomCtrl, maxCtrl, maxUomCtrl, useReplacement);
        if (isInvalid) {
          clearErrorsOnOthers(control, { minControls, maxControls, errorMsg });
          return { [errorMsg]: true };
        }
        // we need to compare each control with null because if enters due to controlsValuesWithReplacement condition, we opened the door
        // to the situation where uom controls could not exist
        const otherControls = minAndMaxControlsWithUom.filter(ctrl => ctrl != null && control !== ctrl);
        otherControls.filter(ctrl => ctrl.invalid).forEach(ctrl => removeControlError(ctrl, errorMsg));
      }
      return null;
    };
  }
}

export const clearErrorsOnOthers = (
  control: UntypedFormControl,
  options: { minControls: UntypedFormControl[]; maxControls: UntypedFormControl[]; errorMsg: string }
) => {
  const { minControls, maxControls, errorMsg } = options;

  const isMinControlRunning = minControls.some(c => c === control);

  if (isMinControlRunning && controlContainsError(maxControls, errorMsg)) {
    maxControls.forEach(c => removeControlError(c, errorMsg));
  } else if (!isMinControlRunning && controlContainsError(minControls, errorMsg)) {
    minControls.forEach(c => removeControlError(c, errorMsg));
  }
};
